import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';

import { AppDispatch, store } from 'app/store';

import {
  DossierSectionOptions,
  DossierSectionStatusOptions,
} from 'features/captains-form/slices/constants';
import {
  deleteCrewListPDF,
  deletePassengersListFile,
  Document,
  downloadDocument,
  downloadPassengersList,
  selectDocuments,
  uploadDocument,
  uploadPassengersList,
} from 'features/captains-form/slices/documentsSlice';
import {
  createPortcallStatusLog,
  FileMetadata,
  getPassengers,
  getPortcallStatusLog,
  selectPassengers,
  selectPortallStatusLog,
  State,
  StatusLog,
} from 'features/captains-form/slices/portCallSlice';

import PersonListForm from '../../crew/components/PersonListForm';
import {
  DOCUMENT_CATEGORIES,
  DOCUMENT_TYPE_IDS,
  DOCUMENT_TYPE_NAMES,
  DocumentCategory,
  DocumentSubCategory,
} from '../../documents/constants';

type PassengersProps = {
  isArrival?: boolean;
};

export const PDF_EXTENSION = '.pdf';
const MAX_FILE_SIZE = 10; //MB

const Passengers = ({ isArrival = false }: PassengersProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const nextPath = `/${params.uid}/${isArrival ? 'departure-passengers' : 'marpol/confirm-waste'}`;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pdfFileInputRef = useRef<HTMLInputElement>(null);
  const listType = isArrival ? 'arrival' : 'departure';

  const { arrival, departure } = useSelector((state: State) =>
    selectPassengers(state)
  );
  const portCallDocuments = useSelector(selectDocuments)?.documents;

  const [currentPassengersFile, setCurrentPassengersFile] = useState<
    FileMetadata | null | undefined
  >();
  const [arrivalPassengersListToUpload, setArrivalPassengersListToUpload] =
    useState<File | undefined>();
  const [departurePassengersListToUpload, setDeparturePassengersListToUpload] =
    useState<File | undefined>();

  const [currentPassengersPDF, setCurrentPassengersPDF] = useState<
    Document | undefined
  >();
  const [arrivalPassengersPDFToUpload, setArrivalPassengersPDFToUpload] =
    useState<File | undefined>();
  const [departurePassengersPDFToUpload, setDeparturePassengersPDFToUpload] =
    useState<File | undefined>();

  const [isNotificationDisplaying, setIsNotificationDisplaying] = useState<
    { message: string } | boolean
  >(false);
  const [notificationAlertType, setNotificationAlertType] = useState<
    'success' | 'error'
  >();
  const [notificationTitle, setNotificationTitle] = useState<string>();

  const [fileToDelete, setFileToDelete] = useState<'excel/csv' | 'pdf'>();
  const [isDeleteFileModalOpen, setIsDeleteFileModalOpen] = useState(false);

  const portCallStatusLog: StatusLog[] = useSelector(selectPortallStatusLog);

  const isPassengersListSubmmitedToNMap = useMemo(() => {
    const section = isArrival
      ? DossierSectionOptions.ArrivalPassenger
      : DossierSectionOptions.DeparturePassenger;
    if (portCallStatusLog && portCallStatusLog?.length > 0) {
      const statusLog = portCallStatusLog?.find(
        (log) => log?.section === section
      );
      return (
        statusLog &&
        statusLog?.status === DossierSectionStatusOptions.SubmittedToNmap
      );
    }
    return false;
  }, [isArrival, portCallStatusLog]);

  useEffect(() => {
    if (isArrival) {
      setCurrentPassengersFile(arrival?.fileMetadata);
    } else {
      setCurrentPassengersFile(departure?.fileMetadata);
    }
  }, [arrival?.fileMetadata, departure?.fileMetadata, isArrival]);

  useEffect(() => {
    const cat = isArrival
      ? DOCUMENT_CATEGORIES.PRE_ARRIVAL_DOCUMENTS
      : DOCUMENT_CATEGORIES.PRE_DEPARTURE_DOCUMENTS;
    const docTypeId = isArrival
      ? DOCUMENT_TYPE_IDS.UPLOAD_ARRIVAL_PASSENGERS
      : DOCUMENT_TYPE_IDS.UPLOAD_DEPARTURE_PASSENGERS;
    const categoryDocuments =
      portCallDocuments[cat as keyof typeof portCallDocuments];
    const currentDocuments = categoryDocuments?.filter((doc) => {
      return doc?.documentType.id === docTypeId;
    });
    if (isArrival) {
      setCurrentPassengersPDF(currentDocuments?.[0]);
    } else {
      setCurrentPassengersPDF(currentDocuments?.[0]);
    }
  }, [
    isArrival,
    portCallDocuments,
    portCallDocuments.preArrivalDocuments,
    portCallDocuments.preDepartureDocuments,
  ]);

  const passengersListToUpload = useMemo(() => {
    return isArrival
      ? arrivalPassengersListToUpload
      : departurePassengersListToUpload;
  }, [
    arrivalPassengersListToUpload,
    departurePassengersListToUpload,
    isArrival,
  ]);

  const passengersPDFToUpload = useMemo(() => {
    return isArrival
      ? arrivalPassengersPDFToUpload
      : departurePassengersPDFToUpload;
  }, [arrivalPassengersPDFToUpload, departurePassengersPDFToUpload, isArrival]);

  const currentPassengersListPreview = useMemo(() => {
    if (currentPassengersFile) {
      return {
        filename: currentPassengersFile.filename,
        lastUpdated: currentPassengersFile.uploadDate,
      };
    } else if (passengersListToUpload) {
      return {
        filename: passengersListToUpload?.name,
        lastUpdated: new Date().toString(),
      };
    } else {
      return;
    }
  }, [passengersListToUpload, currentPassengersFile]);

  const currentPassengersPDFPreview = useMemo(() => {
    if (currentPassengersPDF) {
      return {
        filename: currentPassengersPDF.documentType.name,
        lastUpdated: currentPassengersPDF.lastUpdated,
      };
    } else if (passengersPDFToUpload) {
      return {
        filename: passengersPDFToUpload?.name,
        lastUpdated: new Date().toString(),
      };
    } else {
      return;
    }
  }, [passengersPDFToUpload, currentPassengersPDF]);

  const downloadLocalFile = (file: File) => {
    const url = window.URL.createObjectURL(file);
    const link = document.createElement('a');

    link.href = url;
    link.download = file.name;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();

    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  };

  const updateStatusLog = async () => {
    if (params.uid) {
      const isSuccessUpdateStatus = await dispatch(
        createPortcallStatusLog({
          statusLog: {
            uid: params.uid,
            section: isArrival
              ? DossierSectionOptions.ArrivalPassenger
              : DossierSectionOptions.DeparturePassenger,
            status: DossierSectionStatusOptions.SubmittedToNmap,
          },
        })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) {
          return false;
        } else {
          return true;
        }
      });
      if (!isSuccessUpdateStatus) return;
      dispatch(getPortcallStatusLog(params.uid));
    }
  };

  const uploadPassengersListFile = async (file: File) => {
    if (params.uid) {
      try {
        const res = await dispatch(
          uploadPassengersList({
            portcallUid: params.uid,
            file,
            listType,
          })
        ).unwrap();

        dispatch(
          getPassengers({
            uid: params.uid,
            listType,
          })
        );
        return res;
      } catch {
        alertNotification('error', 'Error', 'Upload failed. Please try again.');
      }
    }
  };

  const uploadPassengersPDFFile = async (file: File) => {
    if (params.uid) {
      try {
        return dispatch(
          uploadDocument({
            documentUid: uuidv4(),
            portcallId: params.uid,
            category: isArrival
              ? DOCUMENT_CATEGORIES.PRE_ARRIVAL_DOCUMENTS
              : DOCUMENT_CATEGORIES.PRE_DEPARTURE_DOCUMENTS,
            documentCategory: DocumentCategory.DUE_DOCUMENTS,
            documentSubCategory: DocumentSubCategory.Captain,
            documentTypeId: isArrival
              ? DOCUMENT_TYPE_IDS.UPLOAD_ARRIVAL_PASSENGERS
              : DOCUMENT_TYPE_IDS.UPLOAD_DEPARTURE_PASSENGERS,
            documentTypeName: DOCUMENT_TYPE_NAMES.PASSENGERS_PDF_DOCUMENT,
            file: file,
            isCrewFile: true,
          })
        );
      } catch (error) {
        console.error('Upload failed:', error);
        alertNotification('error', 'Error', 'Upload failed. Please try again.');
      }
    }
  };

  const onSubmit = async () => {
    try {
      if (passengersListToUpload) {
        await uploadPassengersListFile(passengersListToUpload);

        let pdfUploadResult;
        if (passengersPDFToUpload) {
          pdfUploadResult = await uploadPassengersPDFFile(
            passengersPDFToUpload
          );
        }

        const isListUploaded =
          store.getState().documents.passengers.uploadListStatus ===
          'succeeded';
        const isPDFUploaded =
          !passengersPDFToUpload ||
          pdfUploadResult?.meta.requestStatus === 'fulfilled';

        if (isListUploaded && isPDFUploaded) {
          await updateStatusLog();
          navigate(nextPath);
        }
      } else if (currentPassengersFile) {
        let pdfUploadResult;
        if (passengersPDFToUpload) {
          pdfUploadResult = await uploadPassengersPDFFile(
            passengersPDFToUpload
          );
        }

        const isPDFUploaded =
          !passengersPDFToUpload ||
          pdfUploadResult?.meta.requestStatus === 'fulfilled';

        if (isPDFUploaded) {
          await updateStatusLog();
          navigate(nextPath);
        }
      }
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const handleDownloadTemplate = () => {
    if (params.uid) {
      dispatch(
        downloadPassengersList({
          uid: params.uid,
          listType,
          template: true,
        })
      );
    }
  };

  const handleDownloadPassengersList = () => {
    if (currentPassengersFile) {
      if (params.uid) {
        dispatch(
          downloadPassengersList({
            uid: params.uid,
            listType,
            filename: currentPassengersFile?.filename ?? 'passengers-list.xlsx',
          })
        );
      }
    } else {
      if (passengersListToUpload) {
        downloadLocalFile(passengersListToUpload);
      }
    }
  };

  const handleDownloadPassengersPDF = () => {
    if (currentPassengersPDF) {
      if (params.uid) {
        dispatch(
          downloadDocument({
            uiid: currentPassengersPDF.documentUid,
            fileName: currentPassengersPDF.documentType.name,
          })
        );
      }
    } else {
      if (passengersPDFToUpload) {
        downloadLocalFile(passengersPDFToUpload);
      }
    }
  };

  const handleDeletePassengersList = () => {
    if (fileToDelete === 'excel/csv') {
      handleDeletePassengersListFile();
    } else if (fileToDelete === 'pdf') {
      handleDeletePassengersPDF();
    }
  };

  const handleDeletePassengersListFile = () => {
    if (currentPassengersFile) {
      if (params.uid)
        dispatch(
          deletePassengersListFile({
            uid: params.uid,
            listType,
          })
        ).then(() => {
          const isListDeleted =
            store.getState().documents.deletePassengersListFileStatus ===
            'succeeded';
          if (isListDeleted) {
            setIsDeleteFileModalOpen(false);
            setCurrentPassengersFile(undefined);
            if (isArrival) {
              setArrivalPassengersListToUpload(undefined);
            } else {
              setDeparturePassengersListToUpload(undefined);
            }
            alertNotification(
              'success',
              'Success',
              'Passengers list file deleted successfully!'
            );
          } else {
            alertNotification(
              'error',
              'Error',
              'An error occurred, please try again!'
            );
          }
        });
    } else {
      setIsDeleteFileModalOpen(false);
      setCurrentPassengersFile(undefined);
      if (isArrival) {
        setArrivalPassengersListToUpload(undefined);
      } else {
        setDeparturePassengersListToUpload(undefined);
      }
    }
  };

  const handleDeletePassengersPDF = () => {
    if (currentPassengersPDF) {
      if (params.uid) {
        dispatch(
          deleteCrewListPDF({
            uid: params.uid,
            documentUid: currentPassengersPDF?.documentUid,
          })
        ).then(() => {
          const isPDFDeleted =
            store.getState().documents.deleteCrewListPDFStatus === 'succeeded';
          if (isPDFDeleted) {
            setIsDeleteFileModalOpen(false);
            setCurrentPassengersPDF(undefined);
            if (isArrival) {
              setArrivalPassengersPDFToUpload(undefined);
            } else {
              setDeparturePassengersPDFToUpload(undefined);
            }
            alertNotification(
              'success',
              'Success',
              'Passengers list file deleted successfully!'
            );
          } else {
            alertNotification(
              'error',
              'Error',
              'An error occurred, please try again!'
            );
          }
        });
      }
    } else {
      setIsDeleteFileModalOpen(false);
      setCurrentPassengersPDF(undefined);
      if (isArrival) {
        setArrivalPassengersPDFToUpload(undefined);
      } else {
        setDeparturePassengersPDFToUpload(undefined);
      }
    }
  };

  const alertNotification = (
    type: 'success' | 'error',
    title: string,
    message: string
  ) => {
    setIsNotificationDisplaying({ message: message });
    setNotificationAlertType(type);
    setNotificationTitle(title);
  };

  const handleUploadPassengersList = () => {
    fileInputRef.current?.click();
  };

  const handleUploadPassengersListPDF = () => {
    pdfFileInputRef.current?.click();
  };

  const isValidFile = (file: File, extensions: string) => {
    const fileExtension = file?.name.split('.').pop()?.toLowerCase();

    const isInvalidType = !extensions.includes(fileExtension ?? '');
    if (isInvalidType) {
      alertNotification(
        'error',
        'Error',
        `Unsupported file type. Please upload a file with supported type (${extensions}).`
      );
      return false;
    }

    const maxSizeBytes = MAX_FILE_SIZE * 1024 * 1024;
    const isFileSizeValid = file.size <= maxSizeBytes;
    if (!isFileSizeValid) {
      alertNotification(
        'error',
        'Error',
        `File is too large. Maximum allowed size is ${MAX_FILE_SIZE}MB.`
      );
      return false;
    }
    return true;
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    extensions: string
  ) => {
    const file = event.target.files?.[0];
    if (file && params.uid) {
      if (isValidFile(file, extensions)) {
        if (isArrival) {
          setArrivalPassengersListToUpload(file);
        } else {
          setDeparturePassengersListToUpload(file);
        }
      }
    }
  };

  const handlePDFFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    extensions: string
  ) => {
    const file = event.target.files?.[0];
    if (file && params.uid) {
      if (isValidFile(file, extensions)) {
        if (isArrival) {
          setArrivalPassengersPDFToUpload(file);
        } else {
          setDeparturePassengersPDFToUpload(file);
        }
      }
    }
  };

  return (
    <PersonListForm
      type={'passengers'}
      title={
        isArrival
          ? 'Passengers list on arrival'
          : 'Passengers list on departure'
      }
      nextPath={nextPath}
      backPath={`/${params.uid}/${isArrival ? 'departure-crew' : 'arrival-passengers'}`}
      stepsDisabled={
        !currentPassengersListPreview || isPassengersListSubmmitedToNMap
      }
      isPersonListSubmmitedToNMap={isPassengersListSubmmitedToNMap}
      submitDisabled={
        !currentPassengersListPreview || isPassengersListSubmmitedToNMap
      }
      onSubmit={onSubmit}
      currentPersonListPreview={currentPassengersListPreview}
      currentPersonPDFPreview={currentPassengersPDFPreview}
      handleDownloadTemplate={handleDownloadTemplate}
      handleDownloadPersonList={handleDownloadPassengersList}
      handleDownloadPersonPDF={handleDownloadPassengersPDF}
      handlePDFFileChange={handlePDFFileChange}
      handleFileChange={handleFileChange}
      handleUploadPersonList={handleUploadPassengersList}
      handleUploadPersonListPDF={handleUploadPassengersListPDF}
      fileInputRef={fileInputRef}
      pdfFileInputRef={pdfFileInputRef}
      isDeleteFileModalOpen={isDeleteFileModalOpen}
      setIsDeleteFileModalOpen={setIsDeleteFileModalOpen}
      handleDeletePersonList={handleDeletePassengersList}
      setFileToDelete={setFileToDelete}
      isNotificationDisplaying={isNotificationDisplaying}
      setIsNotificationDisplaying={setIsNotificationDisplaying}
      notificationAlertType={notificationAlertType}
      notificationTitle={notificationTitle}
    />
  );
};

export default Passengers;
